import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import type { SupportMessage } from "@autorepair/contracts";
import { adminApi, ApiError } from "../lib/api";
import { formatDateTime } from "../lib/money";
import {
  ModernBreadcrumbs,
  ModernButton,
  ModernCard,
  ModernErrorBanner,
  ModernField,
  ModernStatusPill,
  ModernSuccessBanner,
  ModernTextarea,
  Spinner,
} from "../components/ui-modern";

/** First name only ("Jane Doe" -> "Jane") for the prefilled salutation —
 * falls back to the full name if it's a single word. */
function firstNameOf(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] || fullName;
}

/** One row in the conversation thread — inbound (the customer) on a
 * neutral background, outbound (us) on a subtly tinted one. Kept
 * deliberately understated (a pale teal, not a saturated chat-bubble
 * color) per the owner's ask. */
function MessageBubble({ message, customerName }: { message: SupportMessage; customerName: string }) {
  const isOutbound = message.direction === "outbound";
  return (
    <div
      className={`rounded-[10px] border p-3.5 ${isOutbound ? "border-[#d4efed] bg-[#eaf6f5]" : "border-[#e2e7ee] bg-white"}`}
    >
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <span className="text-[12.5px] font-bold text-[#071a33]">{isOutbound ? "Bay2Bill Support" : customerName}</span>
        <span className="flex-none text-[11.5px] text-[#8b97a8]">{formatDateTime(message.createdAt)}</span>
      </div>
      <p className="m-0 whitespace-pre-wrap text-[14px] leading-relaxed text-[#101b2d]">{message.body}</p>
    </div>
  );
}

/** Platform-admin detail view for one support inquiry — the full
 * conversation thread plus a reply box. Sending a reply emails the
 * inquirer directly (see AdminSupportService.reply()) and marks the
 * inquiry replied; a customer's own email reply is picked up separately by
 * InboundReplyPollerService and appears here as another inbound bubble. */
export function AdminSupportInquiryDetailPageModern() {
  const { id } = useParams();
  const inquiryId = Number(id);
  const queryClient = useQueryClient();
  const [replyBody, setReplyBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Prefills a generic salutation/signature once per inquiry, leaving the
  // middle blank for the admin to fill in — never overwrites what the
  // admin has already typed (e.g. on a background refetch).
  const prefilledForId = useRef<number | null>(null);

  const inquiryQuery = useQuery({
    queryKey: ["admin", "support-inquiries", inquiryId],
    queryFn: () => adminApi.getSupportInquiry(inquiryId).then((r) => r.inquiry),
  });

  useEffect(() => {
    if (!inquiryQuery.data || prefilledForId.current === inquiryId) return;
    prefilledForId.current = inquiryId;
    setReplyBody(`Hi ${firstNameOf(inquiryQuery.data.name)},\n\nThanks for reaching out to Bay2Bill!\n\n\n\nBest regards,\nThe Bay2Bill Team`);
  }, [inquiryQuery.data, inquiryId]);

  const replyMutation = useMutation({
    mutationFn: () => adminApi.replySupportInquiry(inquiryId, { replyBody }),
    onSuccess: (result) => {
      queryClient.setQueryData(["admin", "support-inquiries", inquiryId], result.inquiry);
      void queryClient.invalidateQueries({ queryKey: ["admin", "support-inquiries"] });
      setReplyBody("");
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Could not send the reply."),
  });

  if (inquiryQuery.isLoading) return <Spinner />;
  if (inquiryQuery.error || !inquiryQuery.data) return <ModernErrorBanner message="Could not load this inquiry." />;
  const inquiry = inquiryQuery.data;

  return (
    <div className="mx-auto max-w-2xl">
      <ModernBreadcrumbs items={[{ label: "Support Inquiries", to: "/admin/support" }, { label: inquiry.subject }]} />

      <ModernCard className="mb-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="m-0 text-[20px] font-bold text-[#071a33]">{inquiry.subject}</h1>
            <p className="mt-1 text-[13.5px] text-[#5e6b7d]">
              {inquiry.name} &lt;{inquiry.email}&gt; · {inquiry.source === "website" ? "Website" : "In-app"}
              {inquiry.tenantDisplayName ? ` · ${inquiry.tenantDisplayName}` : ""}
            </p>
          </div>
          <ModernStatusPill status={inquiry.status} />
        </div>
      </ModernCard>

      <ModernCard className="mb-4 flex flex-col gap-2.5">
        {inquiry.messages.map((m) => (
          <MessageBubble key={m.id} message={m} customerName={inquiry.name} />
        ))}
      </ModernCard>

      <ModernCard>
        {error && <ModernErrorBanner message={error} />}
        {replyMutation.isSuccess && <ModernSuccessBanner message="Reply sent." />}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            replyMutation.mutate();
          }}
        >
          <ModernField label="Reply" htmlFor="replyBody" required>
            <ModernTextarea
              id="replyBody"
              required
              rows={6}
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
              placeholder={`Emailed directly to ${inquiry.email}`}
            />
          </ModernField>
          <ModernButton type="submit" disabled={replyMutation.isPending || !replyBody.trim()}>
            {replyMutation.isPending ? "Sending…" : "Send reply"}
          </ModernButton>
        </form>
      </ModernCard>
    </div>
  );
}
